/**
 * One paragraph row of the reader: left gutter (joint-segment strip, paragraph number every 10), the text blocks with
 * their marks, the right gutter (margin ticks, brackets of focused channels' flagged stretches). Text is rendered only
 * as Preact text nodes (UX-SEC-01); dynamic geometry uses CSSOM style objects, never a style attribute string.
 */
import { Component, type ComponentChild } from 'preact';
import { CHANNEL_IDS } from '../../engine/channels/types.ts';
import {
	BLOCK_CODE,
	BLOCK_HEADING,
	BLOCK_LIST,
	BLOCK_SCENE_BREAK,
	BLOCK_TABLE,
	type ViewModel,
} from '../../shared/viewmodel.ts';
import { fmtInt } from '../format.ts';
import { READER } from '../strings-reader.ts';
import type { BracketCell } from './flagged.ts';
import { type Piece, depthClass, spansOfParagraph, splitPieces } from './pieces.ts';

/** Channels whose evidence is structural and also gets a margin tick (UX-MAP-08). */
export const STRUCTURE_CHANNELS: readonly string[] = [
	'ST-03',
	'ST-04',
	'NA-01',
	'PROC-07',
	'PROC-02',
];
const STRUCT_IDX = new Set(STRUCTURE_CHANNELS.map((id) => CHANNEL_IDS.indexOf(id as never)));

export interface FindRanges {
	/** Flat [start, end, …] character ranges, ascending. */
	ranges: Uint32Array;
	/** The range index (into ranges / 2) that is current, or −1. */
	current: number;
}

export interface RowProps {
	vm: ViewModel;
	p: number;
	/** Focus layer: channel index → slot (0–2) or −1; null = the all-channels layer. */
	slotOf: Int8Array | null;
	/** Identity of layer + focus, so a change re-renders every row. */
	layerKey: string;
	activeA: number;
	cursorA: number;
	target: boolean;
	/** Bit 1: inside the `hs` stretch; 2: its first row; 4: its last row. */
	hs: number;
	cells: BracketCell[] | undefined;
	segFill: string;
	find: FindRanges | null;
	/** Changes when the find result set or its current match changes. */
	findKey: string;
	/** The paragraph holds a kept-best rewrite (BENCH-V2-SPEC §13.1): it gets its own mark. */
	kept?: boolean;
}

const SLOT_LABEL = ['s1', 's2', 's3'];

function wrapSlots(slots: number, node: ComponentChild): ComponentChild {
	let out = node;
	for (let s = 2; s >= 0; s--) if (slots & (1 << s)) out = <span class={`f${s + 1}`}>{out}</span>;
	return out;
}

/** Split `text` ([a0, b0) of the document) at the find ranges inside it, wrapping each match once. */
function withFind(text: string, a0: number, f: FindRanges | null, key: string): ComponentChild {
	if (!f || f.ranges.length === 0) return text;
	const b0 = a0 + text.length;
	const r = f.ranges;
	// first range ending after a0
	let lo = 0;
	let hi = r.length / 2;
	while (lo < hi) {
		const mid = (lo + hi) >> 1;
		if ((r[mid * 2 + 1] as number) <= a0) lo = mid + 1;
		else hi = mid;
	}
	const out: ComponentChild[] = [];
	let cur = a0;
	for (let i = lo; i < r.length / 2; i++) {
		const s = r[i * 2] as number;
		const e = r[i * 2 + 1] as number;
		if (s >= b0) break;
		const s2 = Math.max(s, a0);
		const e2 = Math.min(e, b0);
		if (s2 > cur) out.push(text.slice(cur - a0, s2 - a0));
		out.push(
			<span
				key={`${key}-${i}`}
				class={i === f.current ? 'rd-find is-current' : 'rd-find'}
				data-find={i}
			>
				{text.slice(s2 - a0, e2 - a0)}
			</span>
		);
		cur = e2;
	}
	if (out.length === 0) return text;
	if (cur < b0) out.push(text.slice(cur - a0));
	return out;
}

function renderText(
	props: RowProps,
	a0: number,
	b0: number,
	ids: readonly number[]
): ComponentChild[] {
	const { vm, slotOf, activeA, cursorA, find, findKey } = props;
	const text = vm.text.slice(a0, b0);
	const pieces: Piece[] = splitPieces(a0, b0, ids, vm.spans, slotOf);
	const out: ComponentChild[] = [];
	let cur = a0;
	for (const pc of pieces) {
		if (pc.a > cur) out.push(withFind(text.slice(cur - a0, pc.a - a0), cur, find, findKey));
		const inner = withFind(text.slice(pc.a - a0, pc.b - a0), pc.a, find, findKey);
		let cls = 'pc';
		if (slotOf) cls += ' pf';
		else cls += ` d${depthClass(pc.depth)}`;
		if (pc.a === activeA) cls += ' is-active';
		if (pc.a === cursorA) cls += ' is-cursor';
		out.push(
			<mark key={pc.a} class={cls} data-a={pc.a} data-b={pc.b} data-spans={pc.spans.join(',')}>
				{slotOf ? wrapSlots(pc.slots, inner) : inner}
			</mark>
		);
		cur = pc.b;
	}
	if (cur < b0) out.push(withFind(text.slice(cur - a0), cur, find, findKey));
	return out;
}

function Ticks({ props, ids }: { props: RowProps; ids: readonly number[] }) {
	const { vm, p, slotOf } = props;
	const cs = vm.paras.charStart[p] as number;
	const len = Math.max(1, (vm.paras.charEnd[p] as number) - cs);
	const seen = new Set<string>();
	const ticks: Array<{ id: string; pct: number }> = [];
	const s0 = vm.spans.paraFirst[p] as number;
	const s1 = vm.spans.paraFirst[p + 1] as number;
	for (const i of ids) {
		if (i < s0 || i >= s1) continue;
		const c = vm.spans.channel[i] as number;
		if (!STRUCT_IDX.has(c)) continue;
		if (slotOf && (slotOf[c] as number) < 0) continue;
		const pct = Math.max(0, Math.min(98, (((vm.spans.start[i] as number) - cs) / len) * 100));
		const id = CHANNEL_IDS[c] as string;
		const k = `${id}:${Math.round(pct / 4)}`;
		if (seen.has(k)) continue;
		seen.add(k);
		ticks.push({ id, pct });
	}
	if (ticks.length === 0) return null;
	return (
		<>
			{ticks.map((t) => (
				<span
					key={`${t.id}-${t.pct}`}
					class="rd-tick"
					title={t.id}
					data-tick={t.id}
					style={{ top: `${t.pct}%` }}
				/>
			))}
		</>
	);
}

function blockBody(props: RowProps, b: number, ids: readonly number[]): ComponentChild {
	const { vm } = props;
	const kind = vm.blocks.kind[b] as number;
	const a0 = vm.blocks.charStart[b] as number;
	const b0 = vm.blocks.charEnd[b] as number;
	if (kind === BLOCK_SCENE_BREAK)
		return (
			<div class="rd-scene" key={b}>
				<span aria-hidden="true">{READER.sceneBreakGlyph}</span>
				<span class="visually-hidden">{READER.sceneBreak}</span>
			</div>
		);
	const kids = renderText(props, a0, b0, ids);
	if (kind === BLOCK_HEADING) {
		const H = (vm.blocks.level[b] as number) <= 2 ? 'h2' : 'h3';
		return (
			<H class={H === 'h2' ? 'rd-h2' : 'rd-h3'} key={b}>
				{kids}
			</H>
		);
	}
	if (kind === BLOCK_CODE)
		return (
			<pre class="rd-code" key={b}>
				{kids}
			</pre>
		);
	if (kind === BLOCK_LIST)
		return (
			<div class="rd-p rd-li" key={b}>
				{kids}
			</div>
		);
	if (kind === BLOCK_TABLE)
		return (
			<div class="rd-p rd-cell" key={b}>
				{kids}
			</div>
		);
	return (
		<p class="rd-p" key={b}>
			{kids}
		</p>
	);
}

export class Row extends Component<RowProps> {
	shouldComponentUpdate(n: RowProps): boolean {
		const o = this.props;
		return !(
			o.vm === n.vm &&
			o.p === n.p &&
			o.layerKey === n.layerKey &&
			o.activeA === n.activeA &&
			o.cursorA === n.cursorA &&
			o.target === n.target &&
			o.hs === n.hs &&
			o.cells === n.cells &&
			o.segFill === n.segFill &&
			o.findKey === n.findKey &&
			o.kept === n.kept
		);
	}

	render(props: RowProps) {
		const { vm, p, target, hs, cells, segFill } = props;
		const ids = spansOfParagraph(vm.spans, p);
		const b0 = vm.paras.blockFirst[p] as number;
		const b1 = vm.paras.blockFirst[p + 1] as number;
		const body: ComponentChild[] = [];
		for (let b = b0; b < b1; b++) body.push(blockBody(props, b, ids));
		if (b1 <= b0)
			body.push(
				<p class="rd-p" key="own">
					{renderText(props, vm.paras.charStart[p] as number, vm.paras.charEnd[p] as number, ids)}
				</p>
			);
		let cls = 'rd-row';
		if (target) cls += ' is-target';
		if (props.kept) cls += ' is-kept-best';
		if (hs & 1) cls += ' in-hs';
		if (hs & 2) cls += ' hs-first';
		if (hs & 4) cls += ' hs-last';
		const num = (p + 1) % 10 === 0 ? fmtInt(p + 1) : '';
		const labels = cells?.flatMap((c) => c.labels) ?? [];
		return (
			<div
				class={cls}
				// biome-ignore lint/a11y/useSemanticElements: a named group that is also a focus target; a fieldset is for form controls
				role="group"
				aria-label={READER.paragraph(fmtInt(p + 1), fmtInt(vm.paras.count))}
				data-p={p}
				{...(props.kept ? { 'data-kept-best': 'true' } : {})}
				tabIndex={-1}
			>
				<div class="rd-gl" aria-hidden="true">
					<span class="rd-seg" style={{ backgroundColor: segFill }} />
					{num ? <span class="rd-num">{num}</span> : null}
				</div>
				<div class="rd-body">
					{body}
					{labels.length > 0 ? (
						<span class="visually-hidden">{READER.flaggedNote(labels.join(', '))}</span>
					) : null}
				</div>
				<div class="rd-gr" aria-hidden="true">
					<Ticks props={props} ids={ids} />
					{cells ? (
						<div class="rd-lanes">
							{[0, 1, 2].map((s) => {
								const c = cells.find((x) => x.slot === s);
								return (
									<span
										key={s}
										class={`rd-br ${SLOT_LABEL[s]}${c ? ' on' : ''}${c?.first ? ' first' : ''}${c?.last ? ' last' : ''}`}
									/>
								);
							})}
							<div class="rd-bls">
								{[0, 1, 2].map((s) => {
									const c = cells.find((x) => x.slot === s);
									return (
										<span key={s} class={`rd-bl ${SLOT_LABEL[s]}`}>
											{c?.labels.join(', ')}
										</span>
									);
								})}
							</div>
						</div>
					) : null}
				</div>
			</div>
		);
	}
}
